/**
 * Gingival trim cut (V7) — height-map carve along the occlusal axis.
 *
 * We remove every band voxel on the GINGIVAL side of the trim curve. Instead of building a
 * dilated cut-solid (which smears the trim height laterally and interpolates it across
 * coarse earcut triangles — over-cutting some spots and under-cutting others), we build a
 * 2D HEIGHT MAP H(a, b) in the plane ⟂ the occlusal axis:
 *
 *   - (a, b) are lateral coordinates in that plane; g is the gingival coordinate along the
 *     occlusal axis (`extrudeDir`), so a 3D point is `a·u + b·v + g·dir`.
 *   - H(a, b) = the gingival coordinate `g` of the trim point NEAREST to (a, b). This is a
 *     Voronoi "curtain": every lateral position adopts the height of the closest point on
 *     the (densely sampled) trim curve. No dilation, no earcut — the height is exact along
 *     the whole curve, and outside the loop (the band's labial/lingual wall) it extrapolates
 *     the nearest margin's true height, which is what the wall should be cut at.
 *
 * Clip: each voxel is projected to (a, b, g); H is sampled BILINEARLY at (a, b); the band
 * SDF is intersected with the half-space below the cut surface via
 * `max(band, g − (H − lift))`, so the marching-cubes crossing lands near the cut surface.
 *
 * Voxel precision alone is not enough, though: marching cubes chamfers the crease where the
 * wall surface meets the cut surface, and the Taubin smoother can sag the rim, both by up to
 * ~a voxel — the rim then hangs visibly below the trim curve. So the cut also returns a mesh
 * POST-CLAMP (`clampGeometry`): after extraction + smoothing, every vertex still on the
 * gingival side of the curtain is projected back onto it along the occlusal axis. Viewed
 * along that axis the shell then ends EXACTLY at the trim height, independent of voxel size.
 */

import {
  BufferAttribute,
  BufferGeometry,
  Float32BufferAttribute,
  Uint32BufferAttribute,
  Vector3,
} from 'three';
import type { VoxelGrid } from '@modules/three/common/voxels';
import type { TrimProfileCurve } from './alignerTrimField';

export type TrimCutOptions = {
  /** Gingival direction (unit) — the trim-plane normal pointing away from the crowns. */
  extrudeDir: Vector3;
  /** Debug-sheet extrusion length (mm). Does not affect the carve. */
  depth: number;
  /** Lateral margin (mm) beyond the trim loop used for the map bounds and debug-sheet extent. */
  outwardMm?: number;
  /**
   * Occlusal lift (mm): lower the cut threshold toward the crowns so a thin extra strip just
   * above the trim is also removed. Lateral coverage is unaffected — this only shifts the g
   * threshold.
   */
  occlusalLiftMm?: number;
};

export type TrimCutResult = {
  grid: VoxelGrid;
  removedVoxels: number;
  /** True when the cut was applied; false when it was skipped (returns input grid). */
  applied: boolean;
  outwardMm: number;
  /** The cut-surface sheet in aligned space (caller owns disposal). Null when skipped. */
  geometry: BufferGeometry | null;
  /**
   * Mesh post-clamp (aligned space): projects every vertex on the gingival side of the
   * (lifted) cut surface back onto it along the occlusal axis, so the extracted/smoothed
   * rim ends exactly at the trim height regardless of voxel size. Mutates the geometry's
   * position attribute in place; returns the number of vertices moved. Null when skipped.
   */
  clampGeometry: ((geometry: BufferGeometry) => number) | null;
  /**
   * Projects the LISTED vertices exactly onto the (lifted) cut surface along the occlusal
   * axis — both directions, unlike the one-sided clamp. Used to pin the rim boundary loop
   * back onto the cut after rim smoothing moved it. Null when the cut was skipped.
   */
  projectOntoCut: ((geometry: BufferGeometry, indices: Iterable<number>) => void) | null;
};

/** Newell's-method normal of a (possibly non-planar) closed loop. */
export function loopNormal(ring: Vector3[]): Vector3 {
  const n = new Vector3();
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i];
    const b = ring[(i + 1) % ring.length];
    n.x += (a.y - b.y) * (a.z + b.z);
    n.y += (a.z - b.z) * (a.x + b.x);
    n.z += (a.x - b.x) * (a.y + b.y);
  }
  if (n.lengthSq() < 1e-12) return new Vector3(0, 0, 1);
  return n.normalize();
}

/** Orthonormal basis (u, v) spanning the plane ⟂ dir, matching the cut-solid convention. */
function planeBasis(dir: Vector3): { u: Vector3; v: Vector3 } {
  const ref = Math.abs(dir.x) < 0.9 ? new Vector3(1, 0, 0) : new Vector3(0, 1, 0);
  const u = new Vector3().crossVectors(ref, dir).normalize();
  const v = new Vector3().crossVectors(dir, u).normalize();
  return { u, v };
}

type HeightField = {
  /** Nearest-trim gingival coordinate per node, row-major [j*cols + i]. */
  g: Float32Array;
  /** Distance (mm) to that nearest trim point per node (for the debug sheet extent). */
  dist: Float32Array;
  cols: number;
  rows: number;
  aMin: number;
  bMin: number;
  cell: number;
  u: Vector3;
  v: Vector3;
  dir: Vector3;
};

/**
 * Build the nearest-trim height field over a regular (a, b) lattice. Each node adopts the
 * gingival coordinate of its nearest trim point, found via a coarse bucket grid with an
 * expanding-ring search (stops once no further ring can beat the current best).
 */
function buildHeightField(
  ring: Vector3[],
  dir: Vector3,
  cell: number,
  marginMm: number,
): HeightField | null {
  const n = ring.length;
  if (n < 3) return null;
  const { u, v } = planeBasis(dir);

  // Project trim points into (a, b, g).
  const pa = new Float32Array(n);
  const pb = new Float32Array(n);
  const pg = new Float32Array(n);
  let aMin = Infinity;
  let aMax = -Infinity;
  let bMin = Infinity;
  let bMax = -Infinity;
  for (let i = 0; i < n; i++) {
    const p = ring[i];
    const a = p.x * u.x + p.y * u.y + p.z * u.z;
    const b = p.x * v.x + p.y * v.y + p.z * v.z;
    pa[i] = a;
    pb[i] = b;
    pg[i] = p.x * dir.x + p.y * dir.y + p.z * dir.z;
    if (a < aMin) aMin = a;
    if (a > aMax) aMax = a;
    if (b < bMin) bMin = b;
    if (b > bMax) bMax = b;
  }

  // Lattice bounds: trim extent + margin so all band voxels project inside (out-of-range
  // lookups clamp to the edge anyway).
  aMin -= marginMm;
  bMin -= marginMm;
  aMax += marginMm;
  bMax += marginMm;
  const cols = Math.max(2, Math.ceil((aMax - aMin) / cell) + 1);
  const rows = Math.max(2, Math.ceil((bMax - bMin) / cell) + 1);

  // Bucket grid over the trim points (a few points per bin) for fast nearest queries.
  const binSize = Math.max(2, cell * 8);
  const binCols = Math.max(1, Math.ceil((aMax - aMin) / binSize));
  const binRows = Math.max(1, Math.ceil((bMax - bMin) / binSize));
  const bins: number[][] = Array.from({ length: binCols * binRows }, () => []);
  for (let i = 0; i < n; i++) {
    const bi = clampInt(Math.floor((pa[i] - aMin) / binSize), binCols);
    const bj = clampInt(Math.floor((pb[i] - bMin) / binSize), binRows);
    bins[bj * binCols + bi].push(i);
  }
  const maxRing = binCols + binRows;

  const g = new Float32Array(cols * rows);
  const dist = new Float32Array(cols * rows);
  for (let j = 0; j < rows; j++) {
    const b = bMin + j * cell;
    const bj = clampInt(Math.floor((b - bMin) / binSize), binRows);
    for (let i = 0; i < cols; i++) {
      const a = aMin + i * cell;
      const bi = clampInt(Math.floor((a - aMin) / binSize), binCols);

      let best = Infinity;
      let bestG = 0;
      for (let r = 0; r <= maxRing; r++) {
        for (let dj = -r; dj <= r; dj++) {
          const cj = bj + dj;
          if (cj < 0 || cj >= binRows) continue;
          const onRowEdge = dj === -r || dj === r;
          for (let di = -r; di <= r; di++) {
            if (!onRowEdge && di !== -r && di !== r) continue; // ring only
            const ci = bi + di;
            if (ci < 0 || ci >= binCols) continue;
            const bucket = bins[cj * binCols + ci];
            for (let k = 0; k < bucket.length; k++) {
              const p = bucket[k];
              const da = a - pa[p];
              const db = b - pb[p];
              const d2 = da * da + db * db;
              if (d2 < best) {
                best = d2;
                bestG = pg[p];
              }
            }
          }
        }
        // No bin in ring r+1 can be closer than r·binSize → stop once best is within reach.
        if (best < Infinity && r * binSize * (r * binSize) >= best) break;
      }
      const node = j * cols + i;
      g[node] = bestG;
      dist[node] = best === Infinity ? Infinity : Math.sqrt(best);
    }
  }

  return { g, dist, cols, rows, aMin, bMin, cell, u, v, dir };
}

function clampInt(value: number, count: number): number {
  return value < 0 ? 0 : value >= count ? count - 1 : value;
}

/** Bilinear sample of the height field at lateral coordinate (a, b) (clamped to the edge). */
function sampleHeight(field: HeightField, a: number, b: number): number {
  const fa = (a - field.aMin) / field.cell;
  const fb = (b - field.bMin) / field.cell;
  const i0 = clampInt(Math.floor(fa), field.cols);
  const j0 = clampInt(Math.floor(fb), field.rows);
  const i1 = clampInt(i0 + 1, field.cols);
  const j1 = clampInt(j0 + 1, field.rows);
  const ta = fa - i0 < 0 ? 0 : fa - i0 > 1 ? 1 : fa - i0;
  const tb = fb - j0 < 0 ? 0 : fb - j0 > 1 ? 1 : fb - j0;
  const g = field.g;
  const g00 = g[j0 * field.cols + i0];
  const g10 = g[j0 * field.cols + i1];
  const g01 = g[j1 * field.cols + i0];
  const g11 = g[j1 * field.cols + i1];
  const top = g00 + (g10 - g00) * ta;
  const bot = g01 + (g11 - g01) * ta;
  return top + (bot - top) * tb;
}

/**
 * Clip the band SDF to the crown side of the trim height map. Operates on a copy of the
 * band grid in insertion-aligned space. The returned `geometry` (debug cut sheet) is NOT
 * disposed — the caller may reuse it for debug rendering and owns disposal.
 */
export function applyTrimCut(
  band: VoxelGrid,
  profile: TrimProfileCurve,
  options: TrimCutOptions,
): TrimCutResult {
  const outwardMm = options.outwardMm ?? 0;
  const lift = options.occlusalLiftMm ?? 0;
  const dir = options.extrudeDir.clone().normalize();
  const { cellSize } = band.info;

  const field = buildHeightField(profile.points, dir, cellSize, Math.max(outwardMm, 1) + cellSize * 2);
  if (!field) {
    return {
      grid: band,
      removedVoxels: 0,
      applied: false,
      outwardMm,
      geometry: null,
      clampGeometry: null,
      projectOntoCut: null,
    };
  }

  const grid = band.clone();
  const removedVoxels = clipBandToHeightField(grid, field, lift);
  const geometry = buildDebugSheet(field, lift, Math.max(outwardMm, 1) + cellSize * 2, options.depth);
  const clampGeometry = (mesh: BufferGeometry) => clampGeometryToHeightField(mesh, field, lift);
  const projectOntoCut = (mesh: BufferGeometry, indices: Iterable<number>) =>
    projectVerticesOntoHeightField(mesh, indices, field, lift);
  return { grid, removedVoxels, applied: true, outwardMm, geometry, clampGeometry, projectOntoCut };
}

/**
 * CSG-intersect the band SDF with the half-space on the crown side of the (lifted) height
 * field: every voxel takes `max(band, g − (H − lift))`. Gingival voxels flip positive with
 * a true distance-to-cut magnitude and band voxels just occlusal of the cut are clamped
 * toward zero, so the marching-cubes crossing interpolates onto the cut surface. Returns
 * the number of band voxels removed (flipped from inside to outside).
 */
function clipBandToHeightField(grid: VoxelGrid, field: HeightField, lift: number): number {
  const { counts, origin, cellSize } = grid.info;
  const { u, v, dir } = field;

  let removed = 0;
  let idx = 0;
  for (let x = 0; x < counts.x; x++) {
    const wx = origin.x + x * cellSize;
    for (let y = 0; y < counts.y; y++) {
      const wy = origin.y + y * cellSize;
      for (let z = 0; z < counts.z; z++) {
        const wz = origin.z + z * cellSize;
        const a = wx * u.x + wy * u.y + wz * u.z;
        const b = wx * v.x + wy * v.y + wz * v.z;
        const g = wx * dir.x + wy * dir.y + wz * dir.z;
        const cut = g - (sampleHeight(field, a, b) - lift);
        if (cut > grid.data[idx]) {
          if (grid.data[idx] < 0 && cut >= 0) removed++;
          grid.data[idx] = cut;
        }
        idx++;
      }
    }
  }
  return removed;
}

/**
 * Mesh post-clamp: project every vertex on the gingival side of the (lifted) cut surface
 * back onto it along the occlusal axis. Marching cubes chamfers the wall/cut crease and the
 * Taubin smoother can sag the rim below the curtain by up to ~a voxel; this pulls those
 * vertices exactly onto the cut surface, so nothing ends below the trim height. Vertices on
 * the crown side are untouched. Mutates the position attribute; returns vertices moved.
 */
function clampGeometryToHeightField(
  geometry: BufferGeometry,
  field: HeightField,
  lift: number,
): number {
  const position = geometry.getAttribute('position') as BufferAttribute | null;
  if (!position) return 0;
  const { u, v, dir } = field;

  let moved = 0;
  for (let i = 0; i < position.count; i++) {
    const x = position.getX(i);
    const y = position.getY(i);
    const z = position.getZ(i);
    const a = x * u.x + y * u.y + z * u.z;
    const b = x * v.x + y * v.y + z * v.z;
    const g = x * dir.x + y * dir.y + z * dir.z;
    const excess = g - (sampleHeight(field, a, b) - lift);
    if (excess > 0) {
      position.setXYZ(i, x - dir.x * excess, y - dir.y * excess, z - dir.z * excess);
      moved++;
    }
  }
  if (moved > 0) position.needsUpdate = true;
  return moved;
}

/**
 * Snap the listed vertices exactly onto the (lifted) cut surface along the occlusal axis,
 * regardless of which side they sit on. The curtain height is re-sampled at each vertex's
 * CURRENT lateral position, so vertices moved laterally by rim smoothing land on the right
 * spot of the cut surface.
 */
function projectVerticesOntoHeightField(
  geometry: BufferGeometry,
  indices: Iterable<number>,
  field: HeightField,
  lift: number,
): void {
  const position = geometry.getAttribute('position') as BufferAttribute | null;
  if (!position) return;
  const { u, v, dir } = field;

  for (const i of indices) {
    const x = position.getX(i);
    const y = position.getY(i);
    const z = position.getZ(i);
    const a = x * u.x + y * u.y + z * u.z;
    const b = x * v.x + y * v.y + z * v.z;
    const g = x * dir.x + y * dir.y + z * dir.z;
    const excess = g - (sampleHeight(field, a, b) - lift);
    position.setXYZ(i, x - dir.x * excess, y - dir.y * excess, z - dir.z * excess);
  }
  position.needsUpdate = true;
}

/**
 * Build a thin debug sheet of the cut surface: a quad mesh over the lattice nodes within
 * `footprintMargin` of the trim curve, each vertex at `a·u + b·v + (H − lift)·dir`, extruded
 * gingivally by `depth` so it reads as a solid in the viewer. Visual aid only.
 */
function buildDebugSheet(
  field: HeightField,
  lift: number,
  footprintMargin: number,
  depth: number,
): BufferGeometry | null {
  const { cols, rows, aMin, bMin, cell, u, v, dir, g, dist } = field;
  const stride = 2; // coarsen for a lighter overlay
  const index = new Int32Array(cols * rows).fill(-1);
  const positions: number[] = [];

  const place = (i: number, j: number): number => {
    const node = j * cols + i;
    if (index[node] >= 0) return index[node];
    const a = aMin + i * cell;
    const b = bMin + j * cell;
    const gg = g[node] - lift;
    const px = u.x * a + v.x * b + dir.x * gg;
    const py = u.y * a + v.y * b + dir.y * gg;
    const pz = u.z * a + v.z * b + dir.z * gg;
    const id = positions.length / 3;
    positions.push(px, py, pz);
    index[node] = id;
    return id;
  };

  const active = (i: number, j: number): boolean =>
    i >= 0 && j >= 0 && i < cols && j < rows && dist[j * cols + i] <= footprintMargin;

  const indices: number[] = [];
  for (let j = 0; j + stride < rows; j += stride) {
    for (let i = 0; i + stride < cols; i += stride) {
      if (!active(i, j) || !active(i + stride, j) || !active(i, j + stride) || !active(i + stride, j + stride)) {
        continue;
      }
      const a00 = place(i, j);
      const a10 = place(i + stride, j);
      const a01 = place(i, j + stride);
      const a11 = place(i + stride, j + stride);
      indices.push(a00, a10, a11, a00, a11, a01);
    }
  }
  if (!indices.length) return null;

  // Extrude gingivally so the sheet has thickness (front + back faces).
  const topCount = positions.length / 3;
  for (let t = 0; t < topCount; t++) {
    positions.push(
      positions[t * 3] + dir.x * depth,
      positions[t * 3 + 1] + dir.y * depth,
      positions[t * 3 + 2] + dir.z * depth,
    );
  }
  const sideCount = indices.length;
  for (let t = 0; t < sideCount; t += 3) {
    const a = indices[t];
    const b = indices[t + 1];
    const c = indices[t + 2];
    indices.push(a + topCount, c + topCount, b + topCount);
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
  geometry.setIndex(new Uint32BufferAttribute(indices, 1));
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}
